/**
 * Chat page - Real-time messaging interface.
 * Displays conversation list and message window.
 */
import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Search, Send, ArrowLeft, MoreVertical } from 'lucide-react';
import chatService, { ChatRoom } from '../../services/chat';
import ConversationList from './ConversationList';
import MessageWindow from './MessageWindow';
import NewChatModal from './NewChatModal';
import BroadcastMessageModal from '../../components/chat/BroadcastMessageModal';
import { toast } from 'react-hot-toast';
import { useWebSocket } from '../../services/websocket';
import { useChatStore } from '../../stores/chatStore';
import { useAuth } from '../../contexts/AuthContext';

const Chat: React.FC = () => {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'direct' | 'group' | 'archived'>('all');

  const { setUnreadCount } = useChatStore();
  const { user } = useAuth();

  // Vérifier si l'utilisateur peut envoyer des messages en diffusion (only admin and super_admin)
  const canBroadcast = user?.role === 'admin' || user?.role === 'super_admin';

  // Check if user is a client (only clients use support room only)
  const isClient = user?.role === 'client';

  const useSupportRoomOnly = isClient; // Only clients use support room only, apporteurs see all rooms

  // Charger les conversations pour admins/super_admins
  const loadRooms = async (isArchived: boolean = false) => {
    try {
      setLoading(true);
      // Load all rooms in a single call (no type filter = all types)
      const allRooms = await chatService.listRooms(undefined, isArchived);
      setRooms(allRooms);
    } catch (error: any) {
      console.error('Error loading rooms:', error);
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  // Charger la room de support pour les clients et apporteurs
  const loadSupportRoom = async () => {
    try {
      setLoading(true);
      const supportRoom = await chatService.getOrCreateClientSupportRoom();
      setRooms([supportRoom]);
      setSelectedRoom(supportRoom);
    } catch (error: any) {
      console.error('Error loading support room:', error);
      toast.error('Erreur lors du chargement de la conversation de support');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (useSupportRoomOnly) {
      loadSupportRoom();
    } else {
      loadRooms();
    }
  }, [useSupportRoomOnly]);

  useEffect(() => {
    const totalUnread = rooms.reduce((acc, room) => acc + (room.unread_count || 0), 0);
    setUnreadCount(totalUnread);
  }, [rooms, setUnreadCount]);

  // WS notifications (global /ws) to update unread counters in real time
  useWebSocket((message) => {
    if (message.type === 'notification' && message.data?.kind === 'chat_new_message') {
      const rid = message.data.room_id as number;
      const preview = message.data.preview as string | undefined;
      const createdAt = message.data.created_at as string | undefined;
      setRooms((prevRooms) => {
        const updated = prevRooms.map((room) => {
          if (room.id !== rid) return room;
          const isCurrentlyOpen = selectedRoom?.id === rid;
          return {
            ...room,
            last_message_at: createdAt || room.last_message_at,
            last_message_preview: preview || room.last_message_preview,
            unread_count: isCurrentlyOpen ? 0 : (room.unread_count || 0) + 1,
          } as ChatRoom;
        });
        // sort by last_message_at desc
        return updated.sort((a, b) => {
          const da = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
          const db = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
          return db - da;
        });
      });
    }
  });

  // Filtrer par recherche et type
  const filteredRooms = rooms.filter((room) => {
    // Type filter
    if (filterType === 'direct' && room.type !== 'DIRECT') return false;
    if (filterType === 'group' && room.type !== 'GROUP') return false;
    if (filterType === 'archived') return true; // Show all archived rooms

    // Search filter
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();

    // Recherche par nom de groupe ou peer_display_name
    if (room.name && room.name.toLowerCase().includes(query)) {
      return true;
    }
    if (room.peer_display_name && room.peer_display_name.toLowerCase().includes(query)) {
      return true;
    }

    // Recherche par dernier message
    if (room.last_message_preview && room.last_message_preview.toLowerCase().includes(query)) {
      return true;
    }

    return false;
  });

  // Gérer sélection d'une conversation
  const handleSelectRoom = (room: ChatRoom) => {
    setSelectedRoom(room);

    // Marquer comme lu
    if (room.unread_count && room.unread_count > 0) {
      chatService.markAsRead(room.id).catch(console.error);

      // Mettre à jour localement
      setRooms(prevRooms =>
        prevRooms.map(r =>
          r.id === room.id ? { ...r, unread_count: 0 } : r
        )
      );
    }
  };

  // Gérer nouveau message reçu
  const handleNewMessage = (roomId: number, message: any) => {
    console.log('handleNewMessage called:', { roomId, message, selectedRoomId: selectedRoom?.id });
    // Mettre à jour le dernier message de la room
    setRooms(prevRooms => {
      const updatedRooms = prevRooms.map(room => {
        if (room.id === roomId) {
          return {
            ...room,
            last_message_at: message.created_at,
            last_message_preview: message.content || '[Média]',
            unread_count: selectedRoom?.id === roomId ? 0 : (room.unread_count || 0) + 1,
          };
        }
        return room;
      });

      // Trier par dernier message
      return updatedRooms.sort((a, b) => {
        const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return dateB - dateA;
      });
    });
  };

  // Client view - simple message window only
  if (isClient) {
    return (
      <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Simple header for clients */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-6 w-6 text-purple-600" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Support</h1>
          </div>
        </div>

        {/* Message window only - no sidebar */}
        <div className="flex-1 flex flex-col">
          {selectedRoom ? (
            <MessageWindow
              room={selectedRoom}
              onNewMessage={(message) => handleNewMessage(selectedRoom.id, message)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Chargement...
                </h3>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Admin/Super_Admin view - Facebook-style mobile interface
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Mobile: Full-screen chat list or conversation */}
      <div className="md:hidden h-full">
        {selectedRoom ? (
          // Mobile conversation view
          <div className="h-full flex flex-col">
            {/* Mobile conversation header */}
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full -ml-2"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {selectedRoom.type === 'DIRECT'
                      ? selectedRoom.peer_display_name || 'Conversation directe'
                      : selectedRoom.name || 'Conversation de groupe'}
                  </h1>
                </div>
                <button className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                  <MoreVertical className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Mobile message window */}
            <MessageWindow
              room={selectedRoom}
              onNewMessage={(message) => handleNewMessage(selectedRoom.id, message)}
              onRoomDeleted={() => {
                setSelectedRoom(null);
                loadRooms();
              }}
            />
          </div>
        ) : (
          // Mobile chat list view
          <div className="h-full flex flex-col">
            {/* Mobile chat list header */}
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Conversations</h1>
                <div className="flex items-center space-x-2">
                  {canBroadcast && (
                    <button
                      onClick={() => setShowBroadcastModal(true)}
                      className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                      title="Diffusion de groupe"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-full"
                    title="Nouvelle conversation"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile search bar */}
            <div className="flex-shrink-0 px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Mobile filters */}
            <div className="flex-shrink-0 px-4 py-2">
              <div className="flex space-x-2 overflow-x-auto">
                <button
                  onClick={() => {
                    setFilterType('all');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${filterType === 'all'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Toutes
                </button>
                <button
                  onClick={() => {
                    setFilterType('direct');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${filterType === 'direct'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Directes
                </button>
                <button
                  onClick={() => {
                    setFilterType('group');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${filterType === 'group'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Groupes
                </button>
              </div>
            </div>

            {/* Mobile conversation list */}
            <div className="flex-1 overflow-y-auto">
              <ConversationList
                rooms={filteredRooms}
                selectedRoom={selectedRoom}
                onSelectRoom={handleSelectRoom}
                loading={loading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop view - keep existing layout */}
      <div className="hidden md:flex md:flex-col md:h-full">
        {/* Desktop Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-6 w-6 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Messagerie</h1>
            </div>

            <div className="flex items-center space-x-3">
              {canBroadcast && (
                <button
                  onClick={() => setShowBroadcastModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Send className="h-5 w-5" />
                  <span>Diffusion de groupe</span>
                </button>
              )}

              <button
                onClick={() => setShowNewChatModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-5 w-5" />
                <span>Nouvelle conversation</span>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Main content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Desktop Sidebar */}
          <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Desktop search and filters */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <button
                  onClick={() => {
                    setFilterType('all');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'all'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Toutes
                </button>
                <button
                  onClick={() => {
                    setFilterType('direct');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'direct'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Directes
                </button>
                <button
                  onClick={() => {
                    setFilterType('group');
                    if (filterType === 'archived') loadRooms(false);
                  }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'group'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Support
                </button>
                <button
                  onClick={() => {
                    setFilterType('archived');
                    loadRooms(true);
                  }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${filterType === 'archived'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  Archivées
                </button>
              </div>
            </div>

            {/* Desktop conversation list */}
            <ConversationList
              rooms={filteredRooms}
              selectedRoom={selectedRoom}
              onSelectRoom={handleSelectRoom}
              loading={loading}
            />
          </div>

          {/* Desktop message area */}
          <div className="flex-1 flex flex-col">
            {selectedRoom ? (
              <MessageWindow
                room={selectedRoom}
                onNewMessage={(message) => handleNewMessage(selectedRoom.id, message)}
                onRoomDeleted={() => {
                  setSelectedRoom(null);
                  loadRooms();
                }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Sélectionnez une conversation
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Choisissez une conversation existante ou créez-en une nouvelle
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal nouvelle conversation */}
      {showNewChatModal && (
        <NewChatModal
          onClose={() => setShowNewChatModal(false)}
          onRoomCreated={(room) => {
            setShowNewChatModal(false);
            setRooms([room, ...rooms]);
            setSelectedRoom(room);
          }}
        />
      )}

      {/* Modal diffusion de groupe */}
      {showBroadcastModal && (
        <BroadcastMessageModal
          onClose={() => setShowBroadcastModal(false)}
          onSuccess={() => {
            loadRooms();
            toast.success('Messages envoyés avec succès');
          }}
        />
      )}
    </div>
  );
};

export default Chat;
